import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Icon } from '@repo/ui';
import { Button, Card, CardHeader, CardBody, Switch, Badge, LastUpdated } from '@repo/ui';
import { showToast } from '@repo/ui';
import type {
  EnhancedTenantConfig,
  QboModuleConfig,
} from '@xtnd/web/shared/types/tenantSettingTypes';
import { tenantSettingsApi } from '@xtnd/web/features/settings/api/tenantSettingsApi';
import { api } from '@xtnd/web/shared/api';
import {
  qboWebhookManagementApi,
  type QueueStatistics,
} from '../api/qboWebhookManagementApi';

// QBO webhook models that support auto-processing
const QBO_WEBHOOK_MODELS = [
  { name: 'invoice', label: 'Invoice', description: 'Invoice creation, updates, and deletions' },
  { name: 'customer', label: 'Customer', description: 'Customer information changes' },
  { name: 'item', label: 'Item', description: 'Product and service item changes' },
  { name: 'payment', label: 'Payment', description: 'Payment transactions' },
  { name: 'bill', label: 'Bill', description: 'Vendor bill changes' },
];

// QueueStatistics interface is now imported from service

export default function QboWebhookSettingsPage() {
  const navigate = useNavigate();

  // Fetch current tenant configuration
  const {
    data: tenantConfig,
    isLoading,
    error,
    refetch: refetchConfig,
  } = useQuery<EnhancedTenantConfig>({
    queryKey: ['tenant-config'],
    queryFn: () => tenantSettingsApi.getCurrentTenantConfig(),
    staleTime: 30 * 1000, // 30 seconds
  });

  const qboModule = tenantConfig?.modules?.qboAPI as QboModuleConfig;

  // Fetch queue statistics using service
  const {
    data: queueStatistics,
    isLoading: queueStatsLoading,
    refetch: refetchQueueStats,
  } = useQuery<QueueStatistics[]>({
    queryKey: ['qbo-queue-statistics'],
    queryFn: () => qboWebhookManagementApi.getQueueStatistics(),
    enabled: Boolean(qboModule?.configured),
    staleTime: 30 * 1000,
  });

  // Auto-processing toggle mutation
  const autoProcessingMutation = useMutation({
    mutationFn: async ({ entityType, enabled }: { entityType: string; enabled: boolean }) => {
      if (enabled) {
        return await qboWebhookManagementApi.enableAutoProcessing(entityType);
      } else {
        return await qboWebhookManagementApi.disableAutoProcessing(entityType);
      }
    },
    onSuccess: (_, variables) => {
      showToast.success(
        `Auto-processing ${variables.enabled ? 'enabled' : 'disabled'} for ${variables.entityType}`
      );
      refetchQueueStats();
    },
    onError: (error: Error, variables) => {
      showToast.error(
        `Failed to ${variables.enabled ? 'enable' : 'disable'} auto-processing: ${error.message}`
      );
    },
  });

  // Manual process single message mutation
  const processSingleMutation = useMutation({
    mutationFn: async (entityType: string) => {
      return await qboWebhookManagementApi.processSingleMessage(entityType);
    },
    onSuccess: (_, entityType) => {
      showToast.success(`Processed 1 message from ${entityType} queue`);
      refetchQueueStats();
    },
    onError: (error: Error) => {
      showToast.error(`Failed to process message: ${error.message}`);
    },
  });

  // Refresh token mutation
  const refreshTokenMutation = useMutation({
    mutationFn: async () => {
      return await api.post('/tenants/current/qbo/refresh-token');
    },
    onSuccess: () => {
      showToast.success('QBO access token refreshed successfully!');
      refetchConfig(); // Refresh tenant config to get updated token info
    },
    onError: (error: any) => {
      console.error('Token refresh failed:', error);
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Token refresh failed';
      showToast.error(`Token refresh failed: ${errorMessage}`);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-xtnd-blue border-t-transparent"></div>
      </div>
    );
  }

  if (error || !tenantConfig) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Icon name="HiExclamation" className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-xtnd-dark-800 dark:text-white mb-2">
            Failed to load configuration
          </h3>
          <p className="text-xtnd-dark-400 dark:text-xtnd-dark-300 mb-4">
            Unable to load tenant configuration. Please try again.
          </p>
          <Button onClick={() => refetchConfig()} variant="outline">
            <Icon name="HiRefresh" className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!qboModule?.configured) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <Icon name="HiInformationCircle" className="h-12 w-12 text-xtnd-blue mx-auto mb-4" />
            <h3 className="text-lg font-medium text-xtnd-dark-800 dark:text-white mb-2">
              QBO Integration Not Configured
            </h3>
            <p className="text-xtnd-dark-400 dark:text-xtnd-dark-300 mb-4">
              Please configure your QuickBooks Online integration first.
            </p>
            <Button onClick={() => navigate('/settings')} variant="primary">
              <Icon name="HiCog" className="h-4 w-4 mr-2" />
              Go to Settings
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-3">
            <Icon name="HiDocumentText" className="h-8 w-8 text-xtnd-blue" />
            <h1 className="text-3xl font-bold text-xtnd-dark-800 dark:text-white">
              QBO Webhook Settings
            </h1>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={() => refreshTokenMutation.mutate()}
            variant="secondary"
            size="sm"
            loading={refreshTokenMutation.isPending}
            disabled={refreshTokenMutation.isPending || !qboModule?.configured}
            leftIcon={<Icon name="HiCog" className="h-4 w-4" />}
          >
            {refreshTokenMutation.isPending ? 'Refreshing...' : 'Refresh Token'}
          </Button>
          <Button
            onClick={() => refetchQueueStats()}
            variant="outline"
            leftIcon={<Icon name="HiRefresh" className="h-4 w-4" />}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Configuration Status */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">
            Configuration Status
          </h3>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3">
              <Icon name="HiCheckCircle" className="h-5 w-5 text-green-500" />
              <span className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-200">
                QBO Integration: <span className="font-medium">Connected</span>
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <Icon name="HiCheckCircle" className="h-5 w-5 text-green-500" />
              <span className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-200">
                Webhook Endpoint: <span className="font-medium">Active</span>
              </span>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Webhook Queue Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">
              Webhook Queue Management
            </h3>
            <div className="flex items-center space-x-2">
              <Button onClick={() => navigate('/qbo-webhook-activity')} variant="outline" size="sm">
                View Activity
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <div className="space-y-4">
            {QBO_WEBHOOK_MODELS.map(model => {
              const queueStats = queueStatistics?.find(q => q.entityType === model.name);
              const hasMessages = (queueStats?.messagesAvailable || 0) > 0;

              return (
                <div
                  key={model.name}
                  className="border border-xtnd-dark-100 dark:border-xtnd-dark-600 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-base font-medium text-xtnd-dark-800 dark:text-white">
                          {model.label}
                        </h4>
                        {queueStats?.autoProcessingEnabled ? (
                          <Badge variant="success" size="sm">
                            Auto-Processing
                          </Badge>
                        ) : (
                          <Badge variant="secondary" size="sm">
                            Manual
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300 mb-3">
                        {model.description}
                      </p>

                      {queueStats && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div>
                            <div className="text-2xl font-semibold text-xtnd-dark-800 dark:text-white">
                              {queueStats.messagesAvailable}
                            </div>
                            <div className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">
                              Messages Available
                            </div>
                          </div>
                          <div>
                            <div className="text-2xl font-semibold text-xtnd-dark-800 dark:text-white">
                              {queueStats.messagesInFlight}
                            </div>
                            <div className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">
                              In Flight
                            </div>
                          </div>
                          <div className="col-span-2">
                            {queueStats.lastProcessed && (
                              <LastUpdated
                                timestamp={queueStats.lastProcessed}
                                label="Last processed"
                                className="text-xs"
                              />
                            )}
                          </div>
                        </div>
                      )}

                      {/* Queue Controls */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xtnd-dark-500 dark:text-xtnd-dark-300">Auto-Processing:</span>
                          <Switch
                            checked={queueStats?.autoProcessingEnabled || false}
                            onChange={enabled =>
                              autoProcessingMutation.mutate({
                                entityType: model.name,
                                enabled,
                              })
                            }
                            disabled={autoProcessingMutation.isPending || queueStatsLoading}
                          />
                        </div>

                        {hasMessages && !queueStats?.autoProcessingEnabled && (
                          <Button
                            onClick={() => processSingleMutation.mutate(model.name)}
                            size="sm"
                            variant="outline"
                            disabled={processSingleMutation.isPending}
                            className="w-full text-xs"
                          >
                            {processSingleMutation.isPending ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-2 border-current border-t-transparent mr-2"></div>
                                Processing...
                              </>
                            ) : (
                              <>
                                <Icon name="HiPlay" className="h-3 w-3 mr-1" />
                                Process One Message
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {queueStatsLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-xtnd-blue border-t-transparent"></div>
              <span className="ml-2 text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
                Loading queue statistics...
              </span>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Help Information */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">
            About Auto-Processing
          </h3>
        </CardHeader>
        <CardBody>
          <div className="prose prose-sm dark:prose-invert max-w-none text-xtnd-dark dark:text-xtnd-dark-200">
            <p>
              Auto-processing automatically handles webhook messages from QuickBooks Online as they
              arrive. When enabled, messages are processed immediately without manual intervention.
            </p>
            <ul>
              <li>
                <strong>Enabled:</strong> Messages are processed automatically by Lambda functions
              </li>
              <li>
                <strong>Disabled:</strong> Messages accumulate in queues and require manual
                processing
              </li>
              <li>
                <strong>Queue Statistics:</strong> Show current message counts and processing status
              </li>
            </ul>
            <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
              Note: Auto-processing requires proper Lambda event source mapping configuration.
            </p>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
